import zoomableImageTemplate from './zoomable-image.tpl.html';
import './zoomable-image.less';
import {
  WINDOW_EVT_RESIZE,
  windowEventHelperModule,
} from '../../helpers/window-event-helper/window-event-helper';
import { domHelperModule } from '../../helpers/dom-helper/dom-helper';
import { scrollOnDragDirectiveModule } from '../scroll-on-drag/scroll-on-drag';
import { UI_LAYOUT_EVT_RESIZE } from '../ui-layout/ui-layout';
import { stopEvent } from '../../utils/event-utils';

export const zoomableImageComponentModule = angular
  .module('Q.components.zoomable-image', [
    windowEventHelperModule,
    domHelperModule,
    scrollOnDragDirectiveModule,
  ])

  .component('zoomableImage', {
    bindings: {
      zoomDisabled: '<',
    },
    controller: 'ZoomableImageCtrl',
    template: zoomableImageTemplate,
    transclude: true,
  })

  .controller(
    'ZoomableImageCtrl',
    function ZoomableImageCtrl(
      $scope,
      $element,
      $window,
      $document,
      $timeout,
      domHelper,
    ) {
      const ctrl = this;

      const SCROLLBOX_CLASS = '.zoomable-image-scrollbox';
      const CONTROLS_CLASS = '.zoomable-image-controls';

      let zoomLevel = -2;

      ctrl.$onInit = () => {
        _initializeImage();
        $timeout(() => {
          // timeout ensures the DOM has finished rendering
          domHelper
            .whenImageLoaded($element.find('image'))
            .then(_initializeImage);
        });

        ctrl.$onChanges = (changes) => {
          if (changes.zoomDisabled) {
            _initializeImage();
          }
        };
      };

      ctrl.expandOrContract = stopEvent(() => {
        ctrl.expanded = !ctrl.expanded;
        if (ctrl.expanded) {
          _expand();
        } else {
          _contract();
        }
      });

      ctrl.zoomOut = stopEvent(() => {
        _adjustZoomLevel(-1);
      });

      ctrl.zoom100 = stopEvent(() => {
        _adjustZoomLevel(0);
      });

      ctrl.zoomIn = stopEvent(() => {
        _adjustZoomLevel(1);
      });

      ctrl.autoZoom = stopEvent(() => {
        if (ctrl.enabled) {
          _adjustZoomLevel(zoomLevel > 1 ? -10 : 1);
        }
      });

      let lastImageUrl;
      let lastWidth;
      let listeningToEvents = false;

      function _reset() {
        ctrl.expanded = ctrl.enabled = false;
        ctrl.zoomInDisabled = ctrl.zoomOutDisabled = true;
        $element.removeClass('enabled zoomed expanded');
        // revert everything to default CSS
        $element.css('height', null);
        $element.querySelector(SCROLLBOX_CLASS).css({
          left: null,
          right: null,
        });
        $element.querySelector(CONTROLS_CLASS).css({
          left: null,
        });
        $element.find('img').css({
          width: null,
          height: null,
          'max-width': null,
        });
      }

      function _initializeImage() {
        const $img = $element.find('img');
        const img = $img[0];

        if (!img || ctrl.zoomDisabled) {
          lastImageUrl = null;
          _reset();
          return;
        }
        if (
          ctrl.enabled &&
          img.src === lastImageUrl &&
          $element.offsetWidth() === lastWidth
        ) {
          if (ctrl.expanded) {
            _expand();
          } else {
            _contract();
          }
          return;
        }

        _reset();

        lastWidth = $element.offsetWidth();
        lastImageUrl = img.src;

        if (!listeningToEvents) {
          listeningToEvents = true;
          $scope.$on(WINDOW_EVT_RESIZE, _initializeImage);
          $scope.$on(UI_LAYOUT_EVT_RESIZE, _initializeImage);
          $scope.$watch(
            () => $element[0].offsetWidth + img.src,
            _initializeImage,
          );
        }

        if (img.naturalWidth > $element.offsetWidth() * 0.9) {
          // if the image is greater than 90% of the available width, enable the tool
          ctrl.enabled = true;
          $element.addClass('enabled');
          _contract();
        }
      }

      function _expand() {
        const elOffset = $element.offset();
        const maxBoundary = _maxBoundary();
        const $img = $element.find('img');
        const img = $img[0];
        if (img && img.naturalHeight < maxBoundary.height) {
          // don't bother expanding above the image's natural height
          maxBoundary.height = img.naturalHeight + 2;
        }

        // now allow the scrollbox to expand up to our max boundary
        $element.addClass('expanded');
        $element.css('height', `${maxBoundary.height}px`);
        const left = maxBoundary.left - elOffset.left;
        $element.querySelector(SCROLLBOX_CLASS).css({
          left: `${left}px`,
          right: `${elOffset.right - maxBoundary.right}px`,
        });
        $element.querySelector(CONTROLS_CLASS).css({
          left: `${left}px`,
        });

        // enable the zoom buttons by using _setZoomLevel, zoom to 1:1 by default
        _adjustZoomLevel(0, maxBoundary.width, maxBoundary.height);
        const scrollbox = $element.querySelector(SCROLLBOX_CLASS)[0];
        if (scrollbox && img) {
          $timeout(() => {
            // scroll to center
            scrollbox.scrollLeft = (img.naturalWidth - maxBoundary.width) / 2;
            scrollbox.scrollTop = (img.naturalHeight - maxBoundary.height) / 2;
          }, 200);
        }
      }

      function _contract() {
        $element.removeClass('expanded');
        const $img = $element.find('img');
        const img = $img[0];
        $element.css('height', null);
        const width = $element.offsetWidth();
        let height = $element.offsetHeight();
        if (img) {
          const ratio = img.naturalHeight / img.naturalWidth;
          height = Math.min(img.naturalHeight, width * ratio);
          // default to height of the image
          $element.css('height', `${height}px`);
        }
        // revert sizing mostly to CSS
        $element.querySelector(SCROLLBOX_CLASS).css({
          left: null,
          right: null,
        });
        $element.querySelector(CONTROLS_CLASS).css({
          left: null,
        });
        // enable the zoom buttons by using _setZoomLevel
        _adjustZoomLevel(null, width, height);
      }

      // currently, zoom levels are defined as follows:
      // -2: fits container
      // -1: 50% between container and 1:1
      // 0: 1x
      // 1: 1.5x
      // 2: 2x
      function _adjustZoomLevel(zlAdjustment, scrollboxWidth, scrollboxHeight) {
        if (zlAdjustment === null) {
          zoomLevel = -2;
        } else if (zlAdjustment === 0) {
          zoomLevel = 0;
        } else {
          zoomLevel = Math.min(2, Math.max(-2, zoomLevel + zlAdjustment));
        }

        const $img = $element.find('img');
        const img = $img[0];
        if (!img) return;
        const scrollbox = $element.querySelector(SCROLLBOX_CLASS);
        if (!scrollboxWidth || !scrollboxHeight) {
          scrollboxWidth = scrollbox.offsetWidth();
          scrollboxHeight = scrollbox.offsetHeight();
        }
        const widthRatio = (scrollboxWidth - 2) / img.naturalWidth;
        const heightRatio = (scrollboxHeight - 2) / img.naturalHeight;
        const baseScale = Math.min(widthRatio, heightRatio);

        if (baseScale >= 1 && zoomLevel <= 0) {
          // if we're already 1:1, don't bother shrinking it down
          zoomLevel = 0;
          ctrl.zoomOutDisabled = true;
        } else {
          if (zoomLevel === -1 && baseScale > 0.6) {
            // skip zoomLevel -1 if we're pretty close images
            zoomLevel = zlAdjustment < 0 ? -2 : 0;
          }
          ctrl.zoomOutDisabled = zoomLevel <= -2;
        }
        ctrl.zoomInDisabled = zoomLevel >= 2;
        ctrl.is100 = zoomLevel === 0;
        ctrl.zoomed = !ctrl.zoomOutDisabled;
        $element.toggleClass('zoomed', ctrl.zoomed);

        let scale = baseScale;
        switch (zoomLevel) {
          case -1:
            scale = baseScale / 2 + 0.5; // 50% of fit and 1:1
            break;
          case 0:
            scale = 1;
            break;
          case 1:
            scale = 1.25;
            break;
          case 2:
            scale = 1.5;
            break;
        }
        const height = img.naturalHeight * scale;
        $img.css({
          width: img.naturalWidth * scale + 'px',
          height: height + 'px',
          'max-width': 'none',
        });
      }

      function _maxBoundary() {
        // base boundary
        const scrollParent = $element.scrollableParent();
        let left = scrollParent.offsetLeft();
        let top = scrollParent.offsetTop();
        let width = scrollParent.offsetWidth();
        if (scrollParent[0].clientWidth < width) {
          // accounts for the scrollbar automatically
          width = scrollParent[0].clientWidth;
        }
        let height = scrollParent.offsetHeight();

        const $win = angular.element($window);
        const $sidebar = $document.querySelector('.sidebar');
        const $navbar = $document.querySelector('.navbar-fixed-top');
        const visibleWidth =
          $win.offsetWidth() -
          ($sidebar.isVisible() ? $sidebar.offsetWidth() : 0);
        const visibleHeight =
          $win.offsetHeight() -
          ($navbar.isVisible() ? $navbar.offsetHeight() : 0);

        // adjust to visible boundary (excluding sidebar and navbar)
        width = Math.min(visibleWidth || width, width);
        height = Math.min(visibleHeight || height, height);

        // reduce maxBoundary size by 10px on all used sides
        left += 10;
        top += 10;
        width -= 20;
        height -= 20;
        return {
          left,
          top,
          width,
          height,
          right: left + width,
          bottom: top + height,
        };
      }
    },
  );
